// src/App.js
import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import "./App.css";

/* =========================================================
   AUTH (server-side session; 2026-09-24)
   ========================================================= */
// Credentials are checked by the server (/api/login); none are shipped in this bundle.
const FOOTER_TEXT = "Powered by the Red4 DQR Research Lab";
const RESEARCH_THINKING_DELAY_MS = 3200;

/* =========================================================
   API — Flask proxy (Railway)
   - Prod: same origin behind Flask build/
   - Dev : local Flask
   ========================================================= */
const API_BASE_URL =
  process.env.NODE_ENV === "production" ? "" : "http://localhost:5001";
const PRIMARY_ENDPOINT = "/invoke-agent";

async function apiJson(path, options = {}) {
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      ...options,
    });
    let data = {};
    try { data = await res.json(); } catch (_) {}
    return { ok: res.ok, status: res.status, data };
  } catch (_) {
    return { ok: false, status: 0, data: {} };
  }
}

async function callAgent(payload) {
  // Always return a predictable object
  try {
    const res = await fetch(`${API_BASE_URL}${PRIMARY_ENDPOINT}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const contentType = res.headers.get("content-type") || "";
    const rawText = await res.text();

    // Try JSON first (preferred)
    if (contentType.includes("application/json")) {
      try {
        const data = JSON.parse(rawText || "{}");
        return {
          ok: res.ok,
          status: res.status,
          answer: data.answer ?? data.output ?? data.response ?? "",
          error: data.error ?? "",
          debug: data.debug ?? null,
          raw: data.raw ?? null,
        };
      } catch (_) {
        // JSON header lied; fall through
      }
    }

    // Non-JSON response (fallback)
    return {
      ok: res.ok,
      status: res.status,
      answer: rawText || "",
      error: res.ok ? "" : `HTTP ${res.status}`,
      debug: null,
      raw: rawText || "",
    };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      answer: "",
      error: "Connection error. Flask unreachable or blocked by CORS.",
      debug: String(e),
      raw: null,
    };
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function holdForResearchThinking(startedAt) {
  const remaining = RESEARCH_THINKING_DELAY_MS - (Date.now() - startedAt);
  if (remaining > 0) {
    await wait(remaining);
  }
}

/* =========================================================
   SHARED COMPONENTS
   ========================================================= */
function LogoBlock() {
  return (
    <img src="/Red4 Dev Assist.jpg" alt="Red4" className="red4-logo" />
  );
}

function BackButton() {
  const navigate = useNavigate();
  return (
    <div className="back-row">
      <button className="ghost-link" onClick={() => navigate(-1)}>
        ← Back
      </button>
    </div>
  );
}

function Footer({ text }) {
  return <footer className="red4-credit">{text}</footer>;
}

function PageShell({ children, withLogo = true }) {
  return (
    <main className="red4-page">
      <motion.section
        className="red4-shell"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {withLogo && <LogoBlock />}
        {children}
      </motion.section>
    </main>
  );
}

function AnswerPanel({ busy, err, answer, label = "Thinking" }) {
  if (!busy && !err && !answer) return null;

  return (
    <section className="red4-answer">
      {busy && <div className="red4-loading">{label}</div>}
      {!busy && err && <div className="red4-error">{err}</div>}
      {!busy && answer && <ReactMarkdown>{answer}</ReactMarkdown>}
    </section>
  );
}

/* =========================================================
   LOGIN (auth gate + autofill-safe)
   ========================================================= */
function Login({ setAuth }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const submit = async () => {
    setErr("");
    const r = await apiJson("/api/login", {
      method: "POST",
      body: JSON.stringify({ username: user.trim(), password: pass }),
    });
    if (r.ok) {
      setAuth(true);
      navigate("/dashboard");
      return;
    }
    setErr(r.data.error || (r.status === 0 ? "Connection error." : "Invalid credentials."));
  };

  return (
    <PageShell>
      <form className="auth-box" onSubmit={(event) => event.preventDefault()}>
        <input
          className="red4-input"
          placeholder="User ID"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          autoComplete="username"
        />
        <input
          className="red4-input"
          placeholder="Password"
          type="password"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          autoComplete="current-password"
        />
        <button className="red4-primary" onClick={submit}>
          Enter
        </button>

        {err && <div className="red4-error inline-error">{err}</div>}
      </form>

      <Footer text={FOOTER_TEXT} />
    </PageShell>
  );
}

/* =========================================================
   DASHBOARD
   ========================================================= */
function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  return (
    <PageShell>
      <nav className="companion-actions" aria-label="Red4 ECI research areas">
          <button className="red4-primary" onClick={() => navigate("/research-foundations")}>
            Research Foundations
          </button>

          <button
            className="red4-secondary"
            onClick={() => navigate("/open-search")}
          >
            Open Search
          </button>
      </nav>

      <div className="session-row">
          <button onClick={onLogout} className="ghost-link">
            Log out
          </button>
      </div>

      <Footer text={FOOTER_TEXT} />
    </PageShell>
  );
}

/* =========================================================
   RESEARCH FOUNDATIONS (5 prompts, OXFORD-READY)
   ========================================================= */
function ResearchFoundations() {
  const starters = useMemo(
    () => [
      "What is Enterprise Cybernetic Intelligence?",
      "What interaction problems does ECI address?",
      "What problem does Cognitive Imprinting solve?",
      "What makes systems self-correcting?",
      "How does interaction framing preserve human agency?",
    ],
    []
  );

  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ask = async (q) => {
    const startedAt = Date.now();
    setBusy(true);
    setErr("");
    setAnswer("");

    const r = await callAgent({ query: q, context: "research-starters" });
    await holdForResearchThinking(startedAt);

    if (!r.ok) {
      setErr(r.error || "Request failed.");
      setAnswer(r.answer || "");
      setBusy(false);
      return;
    }

    const a = (r.answer || "").trim();
    if (!a) setErr("Empty answer returned from backend.");
    setAnswer(a);
    setBusy(false);
  };

  return (
    <PageShell>
        <BackButton />
        <h1 className="red4-title">Research Foundations</h1>

        <div className="starter-grid">
          {starters.map((s, i) => (
            <button key={i} className="red4-secondary" onClick={() => ask(s)}>
              {s}
            </button>
          ))}
        </div>

      <AnswerPanel busy={busy} err={err} answer={answer} />

      <Footer text={FOOTER_TEXT} />
    </PageShell>
  );
}

/* =========================================================
   OPEN SEARCH
   ========================================================= */
function OpenSearch() {
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ask = async () => {
    if (!query.trim()) return;
    setBusy(true);
    setErr("");
    setAnswer("");

    const r = await callAgent({ query, context: "corpus-query" });

    if (!r.ok) {
      setErr(r.error || "Request failed.");
      setAnswer(r.answer || "");
      setBusy(false);
      return;
    }

    const a = (r.answer || "").trim();
    if (!a) setErr("Empty answer returned from backend.");
    setAnswer(a);
    setBusy(false);
  };

  return (
    <PageShell>
        <BackButton />
        <h1 className="red4-title">Open Search</h1>

        <form className="ask-box" onSubmit={(event) => { event.preventDefault(); ask(); }}>
        <textarea
          className="red4-textarea"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the Red4 ECI and Cognitive Imprinting research corpus…"
        />

        <button className="red4-primary" disabled={busy || !query.trim()} type="submit">
          Search
        </button>
        </form>

      <AnswerPanel busy={busy} err={err} answer={answer} />

      <Footer text={FOOTER_TEXT} />
    </PageShell>
  );
}

/* =========================================================
   ROOT APP
   ========================================================= */
function App() {
  const [auth, setAuth] = useState(null); // null = checking the server session

  // Global UI hardening (fonts + autofill fix + body background)
  useEffect(() => {
    document.body.style.margin = 0;
    document.body.style.background = "#000";

    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Autofill fix (removes the "ivory" background on Chrome/Safari)
    const style = document.createElement("style");
    style.innerHTML = `
      input, textarea { -webkit-text-fill-color: #f4f0ea; caret-color: #fff; }
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      textarea:-webkit-autofill,
      textarea:-webkit-autofill:hover,
      textarea:-webkit-autofill:focus {
        -webkit-text-fill-color: #f4f0ea !important;
        transition: background-color 9999s ease-in-out 0s;
        box-shadow: 0 0 0px 1000px #0a0a0a inset !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
      }
    `;
    document.head.appendChild(style);

    // Restore the server session (also makes deep links work after a reload)
    apiJson("/api/session").then((r) => setAuth(Boolean(r.data.authenticated)));

    return () => {
      try {
        document.head.removeChild(link);
        document.head.removeChild(style);
      } catch (_) {}
    };
  }, []);

  const logout = () => {
    apiJson("/api/logout", { method: "POST" });
    setAuth(false);
  };

  if (auth === null) return null; // brief: waiting for the session check

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login setAuth={setAuth} />} />

        <Route
          path="/dashboard"
          element={auth ? <Dashboard onLogout={logout} /> : <Navigate to="/login" />}
        />

        <Route
          path="/research-foundations"
          element={auth ? <ResearchFoundations /> : <Navigate to="/login" />}
        />

        <Route
          path="/open-search"
          element={auth ? <OpenSearch /> : <Navigate to="/login" />}
        />

        <Route
          path="/eci-foundations"
          element={<Navigate to={auth ? "/research-foundations" : "/login"} />}
        />

        <Route
          path="/canonical-inquiry"
          element={<Navigate to={auth ? "/open-search" : "/login"} />}
        />

        <Route
          path="/experiment-log"
          element={<Navigate to={auth ? "/dashboard" : "/login"} />}
        />

        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}

export default App;
